// Server-sent events over fetch (POST and the session cookie allowed), parsed with eventsource-parser and validated per event with the generated Zod schemas.
import { EventSourceParserStream } from "eventsource-parser/stream";
import type { ZodType } from "zod";

import { apiFetch, reportInvalidResponse } from "@/api/client";
import { ApiProblem } from "@/api/problem";

export interface SseRequest {
  path: string;
  method?: "GET" | "POST";
  body?: unknown;
  signal?: AbortSignal;
  lastEventId?: string;
}

export interface SseMessage<T> {
  id: string | undefined;
  event: string;
  data: T;
}

export function decodeEventData<T>(event: string, data: string, schema: ZodType<T>): T {
  let payload: unknown;
  try {
    payload = JSON.parse(data) as unknown;
  } catch (error) {
    reportInvalidResponse();
    throw ApiProblem.invalidResponse(error);
  }
  const withEvent =
    payload !== null && typeof payload === "object" && !Array.isArray(payload) && !("event" in payload)
      ? { event, ...payload }
      : payload;
  const parsed = schema.safeParse(withEvent);
  if (!parsed.success) {
    reportInvalidResponse();
    throw ApiProblem.invalidResponse(parsed.error);
  }
  return parsed.data;
}

export async function* readEventStream<T>(
  body: ReadableStream<Uint8Array>,
  schema: ZodType<T>,
): AsyncGenerator<SseMessage<T>> {
  const messages = body
    .pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>)
    .pipeThrough(new EventSourceParserStream());
  const reader = messages.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        return;
      }
      const event = value.event ?? "message";
      yield { id: value.id, event, data: decodeEventData(event, value.data, schema) };
    }
  } finally {
    reader.releaseLock();
  }
}

export async function* streamEvents<T>(
  request: SseRequest,
  schema: ZodType<T>,
): AsyncGenerator<SseMessage<T>> {
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  if (request.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (request.lastEventId !== undefined) {
    headers["Last-Event-ID"] = request.lastEventId;
  }
  const response = await apiFetch(request.path, {
    method: request.method ?? (request.body === undefined ? "GET" : "POST"),
    headers,
    ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
    ...(request.signal === undefined ? {} : { signal: request.signal }),
  });
  if (!response.ok) {
    throw await ApiProblem.fromResponse(response);
  }
  if (response.body === null) {
    throw ApiProblem.invalidResponse(new Error("The event stream has no body."));
  }
  yield* readEventStream(response.body, schema);
}
